import { FC, MouseEvent, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { AiIcon } from '../AiIcon';
import {
  Actions,
  Body,
  CancelButton,
  DangerButton,
  Dialog,
  Divider,
  Footer,
  Message,
  Overlay,
  Title,
  Warning
} from './styles';

export interface DeleteChatModalProps {
  chatName: string;
  isDeleting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const DeleteChatModal: FC<DeleteChatModalProps> = ({
  chatName,
  isDeleting,
  onCancel,
  onConfirm
}) => {
  const { t } = useTranslation('activities');

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isDeleting) {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isDeleting, onCancel]);

  return createPortal(
    <Overlay
      onMouseDown={(event: MouseEvent<HTMLDivElement>) => {
        if (event.target === event.currentTarget && !isDeleting) onCancel();
      }}
    >
      <Dialog role="alertdialog" aria-modal="true" aria-labelledby="ai-chat-delete-title">
        <Title id="ai-chat-delete-title">{t('aiChat.deleteModal.title')}</Title>
        <Divider />
        <Body>
          <Message>
            <Trans
              t={t}
              i18nKey="aiChat.deleteModal.message"
              values={{ name: chatName }}
              components={{ bold: <strong /> }}
            />
          </Message>
          <Warning>
            <AiIcon name="dangerAlert" />
            <span>{t('aiChat.deleteModal.warning')}</span>
          </Warning>
        </Body>
        <Footer>
          <Divider />
          <Actions>
            <CancelButton type="button" onClick={onCancel} disabled={isDeleting}>
              {t('aiChat.deleteModal.cancel')}
            </CancelButton>
            <DangerButton type="button" onClick={onConfirm} disabled={isDeleting}>
              {isDeleting && (
                <Spin
                  size="small"
                  indicator={
                    <LoadingOutlined style={{ fontSize: 12, color: '#FFFFFF' }} />
                  }
                />
              )}
              {isDeleting
                ? t('aiChat.deleteModal.deleting')
                : t('aiChat.deleteModal.confirm')}
            </DangerButton>
          </Actions>
        </Footer>
      </Dialog>
    </Overlay>,
    document.body
  );
};
